import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

/**
 * ControlPointRenderer - Renders Bezier control points on 3D planes
 *
 * Visualizes control points for spline editing in the unified 3D view.
 * Shows handle lines connecting control points to anchor points.
 */
export class ControlPointRenderer {
    constructor() {
        // Store control point meshes by spline/path ID
        this.controlPointGroups = new Map();

        // Visual styling - sizes are specified in SCREEN pixels and converted
        // to world units per frame via setViewScale, so points stay the same
        // apparent size at any zoom level.
        this.anchorScreenPx = 6;   // On-screen radius of anchor point sphere
        this.controlScreenPx = 5;  // On-screen radius of bezier handle sphere
        this.worldPerPixel = 1 / 120; // World units per screen pixel (default zoom)
        this.handleLineWidth = 1;

        // Colors
        this.anchorColor = 0x0090A8; // Dim cyan - selectable
        this.controlColor = 0xFF00E5; // Magenta accent (bezier handles)
        this.handleColor = 0x888888; // Gray
        this.selectedColor = 0x00E5FF; // Bright cyan - selected
    }

    /**
     * Set the current view scale so control points render at a constant
     * on-screen size. For the orthographic camera: worldPerPixel = 1 / zoom.
     * @param {number} worldPerPixel - World units per screen pixel
     */
    setViewScale(worldPerPixel) {
        this.worldPerPixel = worldPerPixel;
    }

    /** Anchor sphere radius in world units at the current view scale */
    get anchorPointSize() {
        return this.anchorScreenPx * this.worldPerPixel;
    }

    /** Bezier handle sphere radius in world units at the current view scale */
    get controlPointSize() {
        return this.controlScreenPx * this.worldPerPixel;
    }

    /**
     * Render control points for a spline on a 3D plane
     *
     * @param {Object} spline - SketchSpline instance
     * @param {Object} basePlane - Base plane the spline is on
     * @param {string} splineId - Unique identifier for this spline
     * @param {THREE.Scene} scene - Three.js scene
     * @param {boolean} [selected=false] - Whether this spline is selected
     */
    renderSplineControls(spline, basePlane, splineId, scene, selected = false) {
        // Remove old control points if they exist
        this.removeControls(splineId, scene);

        // For SketchSpline (brush tool), use centrePath points (the middle path, not the offset outline)
        const points = spline.centrePath?.points || spline.points || [];
        if (points.length === 0) {
            return;
        }

        const group = new THREE.Group();

        // Get the plane's position
        const planePosition = basePlane.position;
        const planeNormal = basePlane.normal;

        // Calculate rotation quaternion from plane normal
        // Default plane faces +Z, so we need to rotate from (0,0,1) to planeNormal
        const defaultNormal = new THREE.Vector3(0, 0, 1);
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(defaultNormal, planeNormal);

        // Thick centre line in the accent color: signals the whole line is
        // draggable. Only the END points get anchor spheres; middle points
        // are dragged via the line itself.
        const isSpline = !!spline.centrePath;
        if (isSpline && points.length >= 2) {
            const linePts = [];
            for (let i = 0; i < points.length; i++) {
                const cur = points[i];
                const next = points[i + 1];
                linePts.push({ x: cur.x, y: cur.y });
                if (next && (cur.controlPoint2 || next.controlPoint1)) {
                    const c1 = cur.controlPoint2 || cur;
                    const c2 = next.controlPoint1 || next;
                    for (let t = 0.125; t < 1; t += 0.125) {
                        const t1 = 1 - t;
                        linePts.push({
                            x: t1*t1*t1*cur.x + 3*t1*t1*t*c1.x + 3*t1*t*t*c2.x + t*t*t*next.x,
                            y: t1*t1*t1*cur.y + 3*t1*t1*t*c1.y + 3*t1*t*t*c2.y + t*t*t*next.y
                        });
                    }
                }
            }
            const positions = [];
            for (const lp of linePts) {
                const wp = new THREE.Vector3(lp.x * 0.01, lp.y * 0.01, 0);
                wp.applyQuaternion(quaternion);
                wp.add(planePosition);
                wp.add(planeNormal.clone().multiplyScalar(0.008));
                positions.push(wp.x, wp.y, wp.z);
            }
            const lineGeo = new LineGeometry();
            lineGeo.setPositions(positions);
            const lineMat = new LineMaterial({
                color: selected ? this.selectedColor : this.anchorColor,
                linewidth: 4, // screen px
                depthTest: false,
                transparent: true,
                opacity: selected ? 0.95 : 0.75
            });
            lineMat.resolution.set(window.innerWidth, window.innerHeight);
            const thickLine = new Line2(lineGeo, lineMat);
            thickLine.renderOrder = 998;
            group.add(thickLine);
        }

        // Render each point and its control handles
        points.forEach((point, index) => {
            // BRUSH strokes: anchor spheres only at the two ENDS - middles
            // are dragged via the thick centre line (rubber-band falloff),
            // not per-point. OFFSET paths (SketchShape.OFFSET_SPLINE = 4)
            // and LEGS (SketchSpline.TYPE_LEG = 43) show EVERY point: their
            // points carry per-point radii edited via the Width widget.
            // Offset/leg MIDDLE points render smaller and dimmer so the
            // ends stay the primary affordance.
            const isWidthEditable = spline.type === 4 || spline.type === 43;
            const isEnd = index === 0 || index === points.length - 1;
            if (isSpline && !isWidthEditable && !isEnd &&
                !point.controlPoint1 && !point.controlPoint2) return;
            const isMiddle = isSpline && !isEnd && !point.controlPoint1 && !point.controlPoint2;
            // Convert canvas coordinates (pixels) to world coordinates (units)
            const canvasX = point.x * 0.01;
            const canvasY = point.y * 0.01;

            // Apply plane's rotation and position to place points in 3D space aligned with chair geometry
            const worldPos = new THREE.Vector3(canvasX, canvasY, 0);
            worldPos.applyQuaternion(quaternion);
            worldPos.add(planePosition);

            // Offset slightly forward from plane surface for visibility
            const normalOffset = planeNormal.clone().multiplyScalar(0.01);
            worldPos.add(normalOffset);

            // Render anchor point (main control point on center path).
            // Middle anchors: 70% size, translucent; selection wins.
            const nodeSelected = spline.selectedNodes?.includes(point);
            const anchorRadius = isMiddle && !nodeSelected
                ? this.anchorPointSize * 0.7 : this.anchorPointSize;
            const anchorGeometry = new THREE.SphereGeometry(anchorRadius, 8, 8);
            const anchorMaterial = new THREE.MeshBasicMaterial({
                color: (nodeSelected || (selected && !spline.selectedNodes?.length))
                    ? this.selectedColor : this.anchorColor,
                transparent: isMiddle && !nodeSelected,
                opacity: isMiddle && !nodeSelected ? 0.55 : 1,
                depthTest: false
            });
            const anchorMesh = new THREE.Mesh(anchorGeometry, anchorMaterial);
            anchorMesh.position.copy(worldPos);
            anchorMesh.renderOrder = 1000; // Render on top
            group.add(anchorMesh);

            // Render control point 1 (outgoing bezier handle from this point)
            if (point.controlPoint1) {
                const cp1Canvas = new THREE.Vector3(
                    point.controlPoint1.x * 0.01,
                    point.controlPoint1.y * 0.01,
                    0
                );
                cp1Canvas.applyQuaternion(quaternion);
                cp1Canvas.add(planePosition);
                const normalOffset1 = planeNormal.clone().multiplyScalar(0.01);
                cp1Canvas.add(normalOffset1);

                // Handle line from anchor to control point
                const handleGeometry = new THREE.BufferGeometry().setFromPoints([
                    worldPos.clone(),
                    cp1Canvas.clone()
                ]);
                const handleMaterial = new THREE.LineBasicMaterial({
                    color: this.handleColor,
                    linewidth: this.handleLineWidth,
                    depthTest: false
                });
                const handleLine = new THREE.Line(handleGeometry, handleMaterial);
                handleLine.renderOrder = 999;
                group.add(handleLine);

                // Control point sphere (bezier handle)
                const cpGeometry = new THREE.SphereGeometry(this.controlPointSize, 8, 8);
                const cpMaterial = new THREE.MeshBasicMaterial({
                    color: this.controlColor,
                    depthTest: false
                });
                const cpMesh = new THREE.Mesh(cpGeometry, cpMaterial);
                cpMesh.position.copy(cp1Canvas);
                cpMesh.renderOrder = 1000;
                group.add(cpMesh);
            }

            // Render control point 2 (incoming bezier handle to this point)
            if (point.controlPoint2 && index > 0) {
                const cp2Canvas = new THREE.Vector3(
                    point.controlPoint2.x * 0.01,
                    point.controlPoint2.y * 0.01,
                    0
                );
                cp2Canvas.applyQuaternion(quaternion);
                cp2Canvas.add(planePosition);
                const normalOffset2 = planeNormal.clone().multiplyScalar(0.01);
                cp2Canvas.add(normalOffset2);

                // Handle line from control point to anchor
                const handleGeometry = new THREE.BufferGeometry().setFromPoints([
                    cp2Canvas.clone(),
                    worldPos.clone()
                ]);
                const handleMaterial = new THREE.LineBasicMaterial({
                    color: this.handleColor,
                    linewidth: this.handleLineWidth,
                    depthTest: false
                });
                const handleLine = new THREE.Line(handleGeometry, handleMaterial);
                handleLine.renderOrder = 999;
                group.add(handleLine);

                // Control point sphere (bezier handle)
                const cpGeometry = new THREE.SphereGeometry(this.controlPointSize, 8, 8);
                const cpMaterial = new THREE.MeshBasicMaterial({
                    color: this.controlColor,
                    depthTest: false
                });
                const cpMesh = new THREE.Mesh(cpGeometry, cpMaterial);
                cpMesh.position.copy(cp2Canvas);
                cpMesh.renderOrder = 1000;
                group.add(cpMesh);
            }
        });

        scene.add(group);
        this.controlPointGroups.set(splineId, group);

        return group;
    }

    /**
     * Render control points for a path (simpler, just anchor points)
     *
     * @param {Object} path - SketchPath instance
     * @param {Object} basePlane - Base plane the path is on
     * @param {string} pathId - Unique identifier for this path
     * @param {THREE.Scene} scene - Three.js scene
     * @param {boolean} [selected=false] - Whether this path is selected
     */
    renderPathControls(path, basePlane, pathId, scene, selected = false) {
        // Remove old control points if they exist
        this.removeControls(pathId, scene);

        if (!path.points || path.points.length === 0) {
            return;
        }

        const group = new THREE.Group();

        // Get the plane's position
        const planePosition = basePlane.position;
        const planeNormal = basePlane.normal;

        // Calculate rotation quaternion from plane normal
        const defaultNormal = new THREE.Vector3(0, 0, 1);
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(defaultNormal, planeNormal);

        // Render each point as a simple anchor
        path.points.forEach((point, index) => {
            // Convert canvas coordinates (pixels) to world coordinates (units)
            const canvasX = point.x * 0.01;
            const canvasY = point.y * 0.01;

            // Apply plane's rotation and position to place points in 3D space aligned with chair geometry
            const worldPos = new THREE.Vector3(canvasX, canvasY, 0);
            worldPos.applyQuaternion(quaternion);
            worldPos.add(planePosition);

            // Offset slightly forward from plane surface for visibility
            const normalOffset = planeNormal.clone().multiplyScalar(0.01);
            worldPos.add(normalOffset);

            const geometry = new THREE.SphereGeometry(this.anchorPointSize, 8, 8);
            const material = new THREE.MeshBasicMaterial({
                color: selected ? this.selectedColor : this.anchorColor,
                depthTest: false
            });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.copy(worldPos);
            mesh.renderOrder = 1000;
            group.add(mesh);
        });

        scene.add(group);
        this.controlPointGroups.set(pathId, group);

        return group;
    }

    /**
     * Remove control points for a specific item
     * @param {string} itemId - Spline or path ID
     * @param {THREE.Scene} scene - Three.js scene
     */
    removeControls(itemId, scene) {
        if (this.controlPointGroups.has(itemId)) {
            const group = this.controlPointGroups.get(itemId);

            // Dispose all geometries and materials
            group.traverse(obj => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
            });

            // Remove from the group's ACTUAL parent - groups now live in
            // the overlay frame, and THREE's remove() only detaches direct
            // children (scene.remove was a silent no-op, leaking a group
            // per frame)
            (group.parent || scene).remove(group);
            this.controlPointGroups.delete(itemId);
        }
    }

    /**
     * Clear all control points
     * @param {THREE.Scene} scene - Three.js scene
     */
    clearAll(scene) {
        this.controlPointGroups.forEach((group, itemId) => {
            group.traverse(obj => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
            });
            (group.parent || scene).remove(group);
        });

        this.controlPointGroups.clear();
    }

    /**
     * Update control point visibility
     * @param {boolean} visible - Show or hide all control points
     */
    setVisible(visible) {
        this.controlPointGroups.forEach(group => {
            group.visible = visible;
        });
    }

    /**
     * Highlight a specific control point (for hover/selection)
     * @param {string} itemId - Spline or path ID
     * @param {number} pointIndex - Index of the point to highlight
     * @param {boolean} highlight - Whether to highlight or unhighlight
     */
    highlightPoint(itemId, pointIndex, highlight) {
        if (!this.controlPointGroups.has(itemId)) return;

        const group = this.controlPointGroups.get(itemId);
        // Each point group has anchor + control points, so find the right one
        // This is a simplified version - a full implementation would track meshes by index

        group.children.forEach(child => {
            if (child instanceof THREE.Mesh) {
                if (highlight) {
                    child.material.color.set(this.selectedColor);
                } else {
                    child.material.color.set(this.anchorColor);
                }
            }
        });
    }

    /**
     * Detect control point click using raycasting (3D hit detection)
     * @param {number} mouseX - Mouse X in screen pixels
     * @param {number} mouseY - Mouse Y in screen pixels
     * @param {THREE.Camera} camera - Three.js camera
     * @param {HTMLCanvasElement} canvas - Canvas element
     * @returns {Object|null} - Intersection info or null
     */
    detectControlPointClick(mouseX, mouseY, camera, canvas) {
        const rect = canvas.getBoundingClientRect();

        // Convert screen coordinates to normalized device coordinates (-1 to +1)
        const ndcX = ((mouseX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((mouseY - rect.top) / rect.height) * 2 + 1;

        // Create raycaster
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

        // Collect all control point meshes
        const meshes = [];
        const meshMetadata = new Map(); // Map mesh to its metadata

        this.controlPointGroups.forEach((group, itemId) => {
            group.traverse(obj => {
                if (obj instanceof THREE.Mesh) {
                    meshes.push(obj);

                    // Store metadata about this mesh
                    // We'll need to extract this from the mesh position and item ID
                    meshMetadata.set(obj, {
                        itemId: itemId,
                        position: obj.position.clone()
                    });
                }
            });
        });

        // Perform raycasting
        const intersects = raycaster.intersectObjects(meshes);

        if (intersects.length > 0) {
            const closestIntersect = intersects[0];
            const metadata = meshMetadata.get(closestIntersect.object);

            return {
                mesh: closestIntersect.object,
                point: closestIntersect.point,
                distance: closestIntersect.distance,
                itemId: metadata?.itemId,
                worldPosition: metadata?.position
            };
        }

        return null;
    }

    /**
     * Set depth test mode for all control points
     * @param {boolean} enabled - Whether to enable depth testing
     */
    setDepthTestMode(enabled) {
        this.controlPointGroups.forEach(group => {
            group.traverse(obj => {
                if (obj.material) {
                    obj.material.depthTest = enabled;
                    obj.material.needsUpdate = true;
                }
            });
        });
    }

    /**
     * Cleanup resources
     */
    dispose() {
        this.controlPointGroups.forEach(group => {
            group.traverse(obj => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
            });
        });
        this.controlPointGroups.clear();
    }
}
